import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import Select from 'react-select';
import { arrayMoveImmutable } from 'array-move';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import FilledButton from '../../components/Buttons/FilledButton';
import DotsLoader from '../../components/DotsLoader';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import TrainingPathService from '../../services/TrainingPathService';
import FolderService from '../../services/FolderService';
import { useUiStore } from '../../store';
import { UiContext } from '../../context/UiContext';
import UserService from '../../services/UserService';
import { ORG_ROLES } from '../../constants/main';
import FolderTreeSelector from '../../components/FolderTreeSelector';
import useNavigationBlocker from '../CreateVideoNewPage/hooks/useNavigationBlocker';
import folderIcon from '../../assets/images/my-library/folder.svg';

import classes from './styles.module.scss';

const NODE_TYPES = {
  FOLDER: 'folder',
  MEETING: 'meeting',
};

const TRAINING_PATH_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 12,
    borderColor: state.isDisabled ? 'rgba(0, 0, 0, 0.1)' : 'var(--text)',
    minHeight: 56,
    boxShadow: 'none',
    fontSize: '20px',
    lineHeight: '28px',
    paddingLeft: '4px',
    ':hover': {
      borderColor: state.isDisabled ? 'rgba(0, 0, 0, 0.1)' : 'var(--primary)',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'rgba(122, 178, 255, 0.18)' : undefined,
    color: '#1b1c1f',
    fontSize: '20px',
    lineHeight: '28px',
    padding: '12px 16px',
    cursor: 'pointer',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: '4px',
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#666666',
    fontSize: '20px',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--black)',
    fontSize: '20px',
    lineHeight: '28px',
  }),
};

export default function CreateTrainingPathPage() {
  const navigate = useNavigate();
  const { id: trainingPathId } = useParams();
  const isEditing = Boolean(trainingPathId);
  const queryClient = useQueryClient();
  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );
  const { notifyError, notifySuccess } = useContext(UiContext);

  const [pathName, setPathName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(TRAINING_PATH_STATUS_OPTIONS.find(opt => opt.value === 'active')?.value || TRAINING_PATH_STATUS_OPTIONS[0].value);
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [selectedMeetings, setSelectedMeetings] = useState(new Set());
  const [resourceNames, setResourceNames] = useState(new Map());
  const [folderDescendants, setFolderDescendants] = useState(new Map());
  const [loadingFolderDescendants, setLoadingFolderDescendants] = useState(
    new Set()
  );
  const [orderedResources, setOrderedResources] = useState([]);
  const [draggedResourceIndex, setDraggedResourceIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLeaveConfirmationVisible, setIsLeaveConfirmationVisible] = useState(false);
  const [isDirtyTrackingEnabled, setIsDirtyTrackingEnabled] = useState(false);
  const leaveBlockerRef = useRef(null);
  const initialValuesRef = useRef({
    pathName: '',
    description: '',
    status: TRAINING_PATH_STATUS_OPTIONS.find(opt => opt.value === 'active')?.value || TRAINING_PATH_STATUS_OPTIONS[0].value,
    selectedFolders: new Set(),
    selectedMeetings: new Set(),
    orderedResources: [],
  });

  const { data: currentUser } = useQuery('me', UserService.getMe);
  const { data: trainingPathDetail } = useQuery(
    ['training-path-edit', trainingPathId],
    () => TrainingPathService.getTrainingPath(trainingPathId, 'admin'),
    {
      enabled: isEditing,
      staleTime: 0,
    }
  );
  const canManageStatus = currentUser?.orgRole?.access === ORG_ROLES.OWNER;

  useEffect(() => {
    setBreadcrumbsOptions([
      { label: 'Training Paths', to: '/training-paths' },
      {
        label: isEditing ? 'Edit' : 'Create',
        to: isEditing
          ? `/training-paths/${trainingPathId}/edit`
          : '/create-training-path',
      },
    ]);
  }, [setBreadcrumbsOptions, isEditing, trainingPathId]);

  const isPrefillPending = isEditing && !isPrefilled;

  // Track unsaved changes by comparing current values to initial values
  useEffect(() => {
    if (isPrefillPending || !isDirtyTrackingEnabled) {
      return;
    }

    const initial = initialValuesRef.current;
    const hasNameChanged = pathName.trim() !== initial.pathName.trim();
    const hasDescriptionChanged = description.trim() !== initial.description.trim();
    const hasStatusChanged = status !== initial.status;
    
    // Compare Sets
    const foldersChanged = 
      selectedFolders.size !== initial.selectedFolders.size ||
      Array.from(selectedFolders).some(id => !initial.selectedFolders.has(id)) ||
      Array.from(initial.selectedFolders).some(id => !selectedFolders.has(id));
    
    const meetingsChanged =
      selectedMeetings.size !== initial.selectedMeetings.size ||
      Array.from(selectedMeetings).some(id => !initial.selectedMeetings.has(id)) ||
      Array.from(initial.selectedMeetings).some(id => !selectedMeetings.has(id));
    
    // Compare ordered resources (deep comparison)
    const resourcesChanged = JSON.stringify(orderedResources) !== JSON.stringify(initial.orderedResources);

    const hasChanges = hasNameChanged || hasDescriptionChanged || hasStatusChanged || 
                      foldersChanged || meetingsChanged || resourcesChanged;
    
    setHasUnsavedChanges(hasChanges);
  }, [pathName, description, status, selectedFolders, selectedMeetings, orderedResources, isPrefillPending, isDirtyTrackingEnabled]);

  // Enable dirty tracking after prefill with a small delay to allow derived state to settle
  useEffect(() => {
    if (!isEditing || !isPrefilled || isDirtyTrackingEnabled) {
      return undefined;
    }
    const timer = setTimeout(() => {
      initialValuesRef.current = {
        pathName: pathName.trim(),
        description: description.trim(),
        status,
        selectedFolders: new Set(selectedFolders),
        selectedMeetings: new Set(selectedMeetings),
        orderedResources: JSON.parse(JSON.stringify(orderedResources)),
      };
      setHasUnsavedChanges(false);
      setIsDirtyTrackingEnabled(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    description,
    isEditing,
    isPrefilled,
    isDirtyTrackingEnabled,
    orderedResources,
    pathName,
    selectedFolders,
    selectedMeetings,
    status,
  ]);

  // Browser navigation warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // React Router navigation blocker
  useNavigationBlocker(
    hasUnsavedChanges,
    useCallback(
      ({ proceed }) => {
        leaveBlockerRef.current = { proceed };
        setIsLeaveConfirmationVisible(true);
      },
      []
    )
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      leaveBlockerRef.current = null;
      setIsLeaveConfirmationVisible(false);
    }
  }, [hasUnsavedChanges]);

  const handleConfirmLeave = useCallback(() => {
    if (leaveBlockerRef.current) {
      leaveBlockerRef.current.proceed();
      leaveBlockerRef.current = null;
    }
    setIsLeaveConfirmationVisible(false);
  }, []);

  const handleCancelLeave = useCallback(() => {
    leaveBlockerRef.current = null;
    setIsLeaveConfirmationVisible(false);
  }, []);

  useEffect(() => {
    if (!isEditing || isPrefilled || !trainingPathDetail?.trainingPath) {
      return;
    }

    const detail = trainingPathDetail.trainingPath;
    const initialName = detail.name || '';
    const initialDescription = detail.description || '';
    const initialStatus = detail.status || TRAINING_PATH_STATUS_OPTIONS.find(opt => opt.value === 'active')?.value || TRAINING_PATH_STATUS_OPTIONS[0].value;

    const folderSet = new Set();
    const meetingSet = new Set();
    const ordered = [];
    
    // Sort resources by orderIndex to maintain order
    const sortedResources = [...(detail.Resources || [])].sort(
      (a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)
    );

    sortedResources.forEach((resource) => {
      if (resource.resourceType === NODE_TYPES.FOLDER) {
        folderSet.add(resource.resourceId);
        ordered.push({
          resourceType: NODE_TYPES.FOLDER,
          resourceId: resource.resourceId,
          orderIndex: ordered.length,
        });
      } else if (resource.resourceType === NODE_TYPES.MEETING) {
        meetingSet.add(resource.resourceId);
        ordered.push({
          resourceType: NODE_TYPES.MEETING,
          resourceId: resource.resourceId,
          orderIndex: ordered.length,
        });
      }
    });
    
    setPathName(initialName);
    setDescription(initialDescription);
    setStatus(initialStatus);
    setSelectedFolders(folderSet);
    setSelectedMeetings(meetingSet);
    setOrderedResources(ordered);
    
    // Store initial values for comparison
    initialValuesRef.current = {
      pathName: initialName,
      description: initialDescription,
      status: initialStatus,
      selectedFolders: new Set(folderSet),
      selectedMeetings: new Set(meetingSet),
      orderedResources: JSON.parse(JSON.stringify(ordered)),
    };
    
    setIsPrefilled(true);
    setHasUnsavedChanges(false);
    setIsDirtyTrackingEnabled(false);
  }, [isEditing, isPrefilled, trainingPathDetail]);

  const meetingFolderMap = useMemo(() => {
    const map = new Map();
    (trainingPathDetail?.content || []).forEach((item) => {
      if (item?.id && item?.folder?.id) {
        map.set(item.id, item.folder.id);
      }
    });
    return map;
  }, [trainingPathDetail?.content]);

  // Map of resource IDs to their display names (from trainingPathDetail)
  const resourceNameMap = useMemo(() => {
    const map = new Map(resourceNames);
    
    // Add names from trainingPathDetail content (for meetings)
    if (trainingPathDetail?.content) {
      trainingPathDetail.content.forEach((item) => {
        if (item.id && item.title) {
          map.set(item.id, item.title);
        }
      });
    }
    
    // For folders, we'll need to fetch names when needed or use IDs
    // This is a simplified version - in production you might want to cache folder names
    
    return map;
  }, [resourceNames, trainingPathDetail?.content]);

  const autoExpandFolderIds = useMemo(() => {
    if (!isEditing) {
      return new Set();
    }
    const ids = new Set(selectedFolders);
    selectedMeetings.forEach((id) => {
      const folderId = meetingFolderMap.get(id);
      if (folderId) {
        ids.add(folderId);
      }
    });
    return ids;
  }, [isEditing, selectedFolders, selectedMeetings, meetingFolderMap]);

  const handleResourceNamesUpdate = useCallback((entries) => {
    if (!entries) {
      return;
    }
    setResourceNames((prev) => {
      const next = new Map(prev);
      Object.entries(entries).forEach(([id, name]) => {
        if (id && name) {
          next.set(id, name);
        }
      });
      return next;
    });
  }, []);

  const fetchFolderDescendants = useCallback(
    async (folderId) => {
      if (!folderId) {
        return;
      }
      setLoadingFolderDescendants((prev) => {
        const next = new Set(prev);
        next.add(folderId);
        return next;
      });
      try {
        const data = await FolderService.getFolderDescendants({ id: folderId });
        const meetings = data?.meetings || [];
        setFolderDescendants((prev) => {
          const next = new Map(prev);
          next.set(folderId, meetings);
          return next;
        });
        if (meetings.length) {
          handleResourceNamesUpdate(
            meetings.reduce((acc, meeting) => {
              if (meeting?.id && meeting?.title) {
                acc[meeting.id] = meeting.title;
              }
              return acc;
            }, {})
          );
        }
      } catch (error) {
        notifyError(
          error?.response?.data?.message ||
            'Unable to load folder videos. Please try again.'
        );
      } finally {
        setLoadingFolderDescendants((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    },
    [handleResourceNamesUpdate, notifyError]
  );

  useEffect(() => {
    if (!selectedFolders.size) {
      return;
    }
    selectedFolders.forEach((folderId) => {
      if (
        folderDescendants.has(folderId) ||
        loadingFolderDescendants.has(folderId)
      ) {
        return;
      }
      fetchFolderDescendants(folderId);
    });
  }, [
    fetchFolderDescendants,
    folderDescendants,
    loadingFolderDescendants,
    selectedFolders,
  ]);

  const handleFolderSelection = useCallback((nodeId) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleMeetingSelection = useCallback((id) => {
    setSelectedMeetings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Sync ordered resources when selections change
  useEffect(() => {
    setOrderedResources((prevOrdered) => {
      const newOrdered = [];
      
      // Create a map of existing resources by ID for quick lookup
      const existingMap = new Map(
        prevOrdered.map((r) => [r.resourceId, r])
      );
      
      // Process folders first (maintain existing order)
      const existingFolders = prevOrdered
        .filter((r) => r.resourceType === NODE_TYPES.FOLDER)
        .map((r) => r.resourceId);
      
      // Add existing folders in their current order
      existingFolders.forEach((folderId) => {
        if (selectedFolders.has(folderId)) {
          newOrdered.push(existingMap.get(folderId));
        }
      });
      
      // Add new folders at the end
      Array.from(selectedFolders).forEach((folderId) => {
        if (!existingMap.has(folderId)) {
          newOrdered.push({
            resourceType: NODE_TYPES.FOLDER,
            resourceId: folderId,
            orderIndex: newOrdered.length,
          });
        }
      });

      // Process meetings (maintain existing order)
      const existingMeetings = prevOrdered
        .filter((r) => r.resourceType === NODE_TYPES.MEETING)
        .map((r) => r.resourceId);
      
      // Add existing meetings in their current order
      existingMeetings.forEach((id) => {
        if (selectedMeetings.has(id)) {
          newOrdered.push(existingMap.get(id));
        }
      });
      
      // Add new meetings at the end
      Array.from(selectedMeetings).forEach((id) => {
        if (!existingMap.has(id)) {
          newOrdered.push({
            resourceType: NODE_TYPES.MEETING,
            resourceId: id,
            orderIndex: newOrdered.length,
          });
        }
      });

      // Update orderIndex to be sequential
      return newOrdered.map((r, idx) => ({
        ...r,
        orderIndex: idx,
      }));
    });
  }, [selectedFolders, selectedMeetings]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, index) => {
    setDraggedResourceIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index.toString());
  }, []);

  const handleDragOver = useCallback(
    (e, index) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      if (draggedResourceIndex !== null && draggedResourceIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [draggedResourceIndex]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedResourceIndex === null || draggedResourceIndex === dropIndex) {
        setDraggedResourceIndex(null);
        setDragOverIndex(null);
        return;
      }

      setOrderedResources((prev) => {
        const newOrdered = arrayMoveImmutable(
          prev,
          draggedResourceIndex,
          dropIndex
        ).map((r, idx) => ({
          ...r,
          orderIndex: idx,
        }));
        return newOrdered;
      });
      
      setDraggedResourceIndex(null);
      setDragOverIndex(null);
    },
    [draggedResourceIndex]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedResourceIndex(null);
    setDragOverIndex(null);
  }, []);

  const isCreateDisabled =
    !pathName.trim() ||
    (!selectedFolders.size && !selectedMeetings.size) ||
    isSubmitting ||
    isPrefillPending;

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      
      // Use orderedResources to maintain orderIndex
      const resources = orderedResources.map((resource) => ({
        resourceType: resource.resourceType,
        resourceId: resource.resourceId,
        includeDescendants: resource.resourceType === NODE_TYPES.FOLDER,
        orderIndex: resource.orderIndex,
      }));

      if (isEditing) {
        await TrainingPathService.updateTrainingPath({
          trainingPathId,
          name: pathName.trim(),
          description: description.trim(),
          resources,
          status,
        });
        // Invalidate queries to refresh the data
        await queryClient.invalidateQueries(['training-path-edit', trainingPathId]);
        await queryClient.invalidateQueries(['training-path', trainingPathId]);
        
        // Update initial values to current values after successful save
        initialValuesRef.current = {
          pathName: pathName.trim(),
          description: description.trim(),
          status,
          selectedFolders: new Set(selectedFolders),
          selectedMeetings: new Set(selectedMeetings),
          orderedResources: JSON.parse(JSON.stringify(orderedResources)),
        };
        
        setHasUnsavedChanges(false);
        notifySuccess('Training path updated successfully.');
        // Stay on edit page after update
      } else {
        await TrainingPathService.createTrainingPath({
          name: pathName.trim(),
          description: description.trim(),
          resources,
          status,
        });
        setHasUnsavedChanges(false);
        notifySuccess('Training path created successfully.');
        // Use setTimeout to ensure state updates and blocker re-evaluation happens before navigation
        setTimeout(() => {
          navigate('/training-paths', { replace: true });
        }, 0);
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          'Unable to create training path. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    description,
    isEditing,
    navigate,
    notifyError,
    notifySuccess,
    orderedResources,
    pathName,
    queryClient,
    selectedFolders,
    selectedMeetings,
    status,
    trainingPathId,
  ]);


  const selectedSummary = useMemo(() => {
    const totalFolders = selectedFolders.size;
    const descendantVideos = Array.from(selectedFolders).reduce(
      (sum, folderId) => sum + (folderDescendants.get(folderId)?.length || 0),
      0
    );
    const totalVideos = selectedMeetings.size + descendantVideos;

    if (!totalFolders && !totalVideos) {
      return 'No folders or videos selected yet.';
    }

    const folderCopy =
      totalFolders === 1 ? '1 folder' : `${totalFolders} folders`;
    const videoCopy =
      totalVideos === 1 ? '1 video' : `${totalVideos} videos`;

    if (totalFolders && totalVideos) {
      return `${folderCopy} · ${videoCopy}`;
    }
    return totalFolders ? folderCopy : videoCopy;
  }, [
    folderDescendants,
    selectedFolders,
    selectedMeetings.size,
  ]);

  const submitLabel = useMemo(() => {
    if (isSubmitting) {
      return isEditing ? 'Updating...' : 'Creating...';
    }
    return isEditing ? 'Update' : 'Create';
  }, [isEditing, isSubmitting]);

  return (
    <Layout hasUserBlock>
      <div className={classes.CreateTrainingPathPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.headerTop}>
              <div className={classes.headerMeta}>
                <Breadcrumbs
                  breadcrumbsOptions={[
                    { label: 'Training Paths', to: '/training-paths' },
                    {
                      label: isEditing ? 'Edit' : 'Create',
                      to: isEditing
                        ? `/training-paths/${trainingPathId}/edit`
                        : '/create-training-path',
                    },
                  ]}
                />
                <p className={classes.subtitle}>
                  Select folders and videos to include in your training path. Org
                  owners can assign these paths once created.
                </p>
              </div>
              <FilledButton
                isDisabled={isCreateDisabled}
                onClick={handleSubmit}
              >
                {submitLabel}
              </FilledButton>
            </div>
          </div>

          <div className={classes.layout}>
            <div className={classes.form}>
              <div className={classes.fieldGroup}>
                <span
                  id="trainingPathNameLabel"
                  className={classes.fieldTitle}
                >
                  Training Path Name
                </span>
                <input
                  id="trainingPathName"
                  name="trainingPathName"
                  aria-labelledby="trainingPathNameLabel"
                  placeholder="e.g. Customer Success Onboarding"
                  value={pathName}
                  onChange={(event) => setPathName(event.target.value)}
                />
              </div>
              <div className={classes.fieldGroup}>
                <span
                  id="trainingPathDescriptionLabel"
                  className={classes.fieldTitle}
                >
                  Description (optional)
                </span>
                <textarea
                  id="trainingPathDescription"
                  name="trainingPathDescription"
                  aria-labelledby="trainingPathDescriptionLabel"
                  placeholder="Describe the purpose or audience for this path."
                  rows={4}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
              <div className={classes.fieldGroup}>
                <span
                  id="trainingPathStatusLabel"
                  className={classes.fieldTitle}
                >
                  Status
                </span>
                <Select
                  isDisabled={!canManageStatus}
                  value={TRAINING_PATH_STATUS_OPTIONS.find(
                    (opt) => opt.value === status
                  )}
                  options={TRAINING_PATH_STATUS_OPTIONS}
                  onChange={(option) => setStatus(option.value)}
                  styles={selectStyles}
                  placeholder="Select status"
                />
              </div>
              {currentUser?.orgRole?.access !== ORG_ROLES.OWNER && (
                <span className={classes.helperText}>
                  Only organization owners can change status, publish, and
                  assign training paths.
                </span>
              )}
            </div>

            <div className={classes.treePanel}>
              <div className={classes.treeSection}>
              <div className={classes.treeHeader}>
                <div>
                  <h3>Folders &amp; Videos</h3>
                  <div className={classes.selectionSummary}>{selectedSummary}</div>
                </div>
                {isPrefillPending && (
                  <DotsLoader />
                )}
              </div>
              <div className={classes.treeBody}>
                <FolderTreeSelector
                  showCheckboxes
                  showMeetings
                  selectedFolderIds={selectedFolders}
                  selectedMeetingIds={selectedMeetings}
                  autoExpandFolderIds={autoExpandFolderIds}
                  onResourceNamesUpdate={handleResourceNamesUpdate}
                  onFolderToggle={handleFolderSelection}
                  onMeetingToggle={handleMeetingSelection}
                  isSubmitting={isSubmitting}
                  isPrefillPending={isPrefillPending}
                />
              </div>
            </div>
              
              <div className={classes.playlistOrderSection}>
                <div className={classes.playlistOrderHeader}>
                  <h3 className={classes.playlistOrderTitle}>Playlist Order</h3>
                  {orderedResources.length > 0 && (
                    <p className={classes.playlistOrderSubtitle}>
                      Drag items to reorder them. This determines the order videos appear in the training path.
                    </p>
                  )}
                </div>
                <div className={classes.playlistOrderBody}>
                  {orderedResources.length === 0 ? (
                    <div className={classes.emptyPlaylist}>
                      Videos you select from &quot;Folders &amp; Videos&quot; will appear here, and you will be able to order them.
                    </div>
                  ) : (
                    <>
                    <ul className={classes.orderedList}>
                      {orderedResources.map((resource, index) => {
                        const resourceName = resourceNameMap.get(resource.resourceId) || resource.resourceId;
                        const folderVideos =
                          resource.resourceType === NODE_TYPES.FOLDER
                            ? folderDescendants.get(resource.resourceId) || []
                            : [];
                        const isFolderLoading =
                          resource.resourceType === NODE_TYPES.FOLDER &&
                          loadingFolderDescendants.has(resource.resourceId);
                        const isDragged = draggedResourceIndex === index;
                        const isDragOver = dragOverIndex === index;
                        
                        return (
                          <li
                            key={`${resource.resourceType}-${resource.resourceId}`}
                            className={classes.orderedItem}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                              opacity: isDragged ? 0.5 : 1,
                              cursor: isDragged ? 'grabbing' : 'grab',
                              borderTop: isDragOver && draggedResourceIndex !== null ? '2px solid var(--primary)' : 'none',
                              transform: isDragged ? 'scale(0.98)' : 'none',
                              transition: isDragged ? 'none' : 'all 0.2s ease',
                            }}
                          >
                            <div className={classes.orderedItemContent}>
                              <div className={classes.dragHandle}>
                                <span className={classes.dragIcon}>⋮⋮</span>
                              </div>
                              {resource.resourceType === NODE_TYPES.FOLDER && (
                                <img
                                  src={folderIcon}
                                  alt="Folder"
                                  className={classes.orderedItemIcon}
                                />
                              )}
                              <span className={classes.orderedItemName}>
                                {resourceName}
                              </span>
                              <span className={classes.orderedItemType}>
                                {resource.resourceType === NODE_TYPES.FOLDER
                                  ? 'Folder'
                                  : 'Video'}
                              </span>
                            </div>
                            {resource.resourceType === NODE_TYPES.FOLDER && (
                              <ul className={classes.orderedChildList}>
                                {isFolderLoading && (
                                  <li className={classes.orderedChildItemMuted}>
                                    Loading videos...
                                  </li>
                                )}
                                {!isFolderLoading &&
                                  folderVideos.length > 0 &&
                                  folderVideos.map((meeting) => (
                                    <li
                                      key={`folder-${resource.resourceId}-meeting-${meeting.id}`}
                                      className={classes.orderedChildItem}
                                    >
                                      <span className={classes.orderedChildBullet}>•</span>
                                      <span className={classes.orderedChildName}>
                                        {meeting.title || meeting.name || meeting.id}
                                      </span>
                                    </li>
                                  ))}
                                {!isFolderLoading && folderVideos.length === 0 && (
                                  <li className={classes.orderedChildItemMuted}>
                                    No videos in this folder
                                  </li>
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Confirmation Modal */}
      <ConfirmActionModal
        show={isLeaveConfirmationVisible}
        title="Leave without saving?"
        message="You have unsaved changes. Leaving this page will discard your edits."
        handleClose={handleCancelLeave}
        onConfirm={handleConfirmLeave}
      />
    </Layout>
  );
}

